import React, { ReactNode, createContext, useContext, useState } from 'react';
import { Alert } from '@mui/material';
import { ProgramAttributes } from '@loyalty/shared-common/gamification-api-client';
import { useAlerts } from '@qualifio/ui-commons';
import { useParams } from 'react-router-dom';
import { Loader } from '../shared/layout/Loader';
import { useApi } from '../shared/api-client/ApiClientProvider';
import { useSelectiveEffect } from '../shared/use-selective-effect';

export interface ProgramContextInterface {
  programId: string;
  program: ProgramAttributes;
  setProgram: (program: ProgramAttributes) => void;
}

export const ProgramContext = createContext<ProgramContextInterface>({} as any);

export function useProgram() {
  return useContext(ProgramContext);
}

export function ProgramProvider({ children }: { children: ReactNode }) {
  const { id: programId } = useParams<{ id: string }>();
  const { gamificationApi } = useApi();
  const { notify } = useAlerts();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<any>();
  const [program, setProgram] = useState<ProgramAttributes>({} as any);

  useSelectiveEffect(
    () => {
      setLoading(true);
      if (!programId) {
        throw new Error('Can not find programId');
      }
      gamificationApi
        .getProgram(programId)
        .then(setProgram)
        .catch(err => {
          notify(`${err}`, 'error');
          setError(err);
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [gamificationApi, notify, programId],
    [programId],
  );

  const context = {
    programId: program.id,
    program,
    setProgram,
  };

  return loading ? (
    <Loader />
  ) : error ? (
    <Alert severity="error">{error.toString()}</Alert>
  ) : (
    <ProgramContext.Provider value={context}>{children}</ProgramContext.Provider>
  );
}
